// SendToPcpModal — Task #178 scaffold (office manager ask via Janu).
//
// Triggered from MedStudentClinicPage.onSign as a wrap around Sign & Save:
// after the clinician signs the note, ask whether to auto-send a copy to
// the patient's PCP. AI drafts a brief referral-back letter from the note
// content (reuses the "Draft PCP letter" prompt from AiActionToolbar).
//
// v1.1 fixes (Leejoe 2026-06-28):
//   - "Sign anyway" path when no PCP info on file — does NOT block the
//     sign. Instead generates an ORDER at the note bottom for the remote
//     team to identify + forward (matches the existing orders-row pattern
//     from Recorder A6.2).
//   - Empty PCP name no longer blocks Sign + Send — falls through to the
//     order-creation path automatically.
//   - Clinic-config toggle stub (localStorage `vmai:clinic-pcp-autosend`,
//     default OFF). When OFF the modal still opens but defaults to
//     "Skip — sign only" so primary-care clinics don't get nagged. When
//     ON (consultant clinics + new patient referrals) defaults to Fax.
//
// v2 follow-up tasks (out of scope tonight):
//   - notes-c columns: pcp_name, pcp_fax, pcp_email, pcp_last_sent_at
//     (or separate patient_contacts table — see spec)
//   - Per-patient physician-roster dropdown (PCP / cardio / neuro / etc.)
//     replacing the freeform name input
//   - Backend endpoint POST /api/send-pcp-letter/ — Twilio fax + SendGrid
//     email; logs to pcp_send_log for audit trail
//   - Inbound delivery-receipt webhook → mark sent_at + status on log row
//   - Real clinic-config table (replaces the localStorage stub)
//   - "Make this the preferred contact" prompt after first successful send

import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Input,
  Textarea,
  Radio,
  RadioGroup,
  Stack,
  Spinner,
  useToast,
  FormControl,
  FormLabel,
  Divider,
} from "@chakra-ui/react";
import { complete, AiError } from "../../utils/aiService";
import { getAiKey } from "../../utils/aiKey";
import { getBackendURL } from "../../components/env";
import { getFixturePcps } from "../../utils/testPatients";

const PCP_LETTER_SYSTEM_PROMPT =
  "You are a specialist drafting a brief letter to a patient's primary care physician summarizing today's visit. 1-2 short paragraphs. Plain text. Open with 'Dear PCP,' and close with 'Thank you for the referral. Please reach out with any questions.' No markdown, no fake addresses, no signatures.";

// Clinic-config stub (v1.1). When ON (consultant clinics + new-patient
// referrals) the modal opens defaulted to Fax delivery. When OFF (primary
// care clinics) it opens defaulted to Skip so the clinician doesn't get
// nagged. Real clinic_settings table replaces this in v2.
const CLINIC_AUTOSEND_KEY = "vmai:clinic-pcp-autosend";

function clinicAutoSendDefault() {
  try {
    return localStorage.getItem(CLINIC_AUTOSEND_KEY) === "true";
  } catch (_) {
    return false;
  }
}

function pcpStorageKey(patientId) {
  return `vmai:pcp-contact:${patientId || "unknown"}`;
}

function loadStoredPcp(patientId) {
  try {
    const raw = localStorage.getItem(pcpStorageKey(patientId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function persistStoredPcp(patientId, pcp) {
  try {
    localStorage.setItem(pcpStorageKey(patientId), JSON.stringify(pcp));
  } catch (_) {
    // localStorage full / blocked — silently no-op; modal still works.
  }
}

export default function SendToPcpModal({
  isOpen,
  onClose,
  patient,
  getNoteText,
  onSent,
}) {
  const toast = useToast();
  const patientId = patient?.id || patient?.EHR_ID || "";
  const patientName = patient?.patient_name || "the patient";

  const [pcpName, setPcpName] = useState("");
  const [pcpFax, setPcpFax] = useState("");
  const [pcpEmail, setPcpEmail] = useState("");
  // v1.1: clinic-config decides the default delivery method. Consultant
  // clinics default to Fax (so the workflow stays one click); primary-care
  // clinics default to Skip (so the clinician isn't nagged).
  const [deliveryMethod, setDeliveryMethod] = useState(
    clinicAutoSendDefault() ? "fax" : "skip",
  );
  const [draftLetter, setDraftLetter] = useState("");
  const [isDrafting, setIsDrafting] = useState(false);
  const [draftError, setDraftError] = useState(null);
  const [isSending, setIsSending] = useState(false);

  // Hydrate stored PCP contact when modal opens. NO AI call here — the modal
  // used to auto-draft the letter the moment it opened, which burned a full
  // AI call on every single Sign & Save even though the default delivery is
  // "Skip — sign only" and the draft was thrown away (Leejoe 2026-07-18:
  // "That should only happen if we ask to send to PCP ... and press send").
  // The draft now runs inside handleSend, only on the fax/email paths.
  useEffect(() => {
    if (!isOpen) return;
    // Clear any letter left over from a previous open — with draft-on-send
    // nothing re-drafts on open, so a stale letter (worst case: another
    // patient's) must never linger in the textarea.
    setDraftLetter("");
    setDraftError(null);
    const stored = loadStoredPcp(patientId);
    if (stored) {
      setPcpName(stored.name || "");
      setPcpFax(stored.fax || "");
      setPcpEmail(stored.email || "");
      if (stored.deliveryMethod) setDeliveryMethod(stored.deliveryMethod);
    } else {
      // Task #209: for fixture patients, autofill with the first seeded PCP
      // so the demo flow has plausible default contact info. Only kicks in
      // when no real PCP is stored for this patient.
      const fixturePcp = getFixturePcps(patient?.EHR_ID)[0];
      if (fixturePcp) {
        setPcpName(fixturePcp.name || "");
        setPcpFax(fixturePcp.fax || "");
        setPcpEmail(fixturePcp.email || "");
      }
    }
    // Leejoe 2026-06-29 — INTENTIONALLY exclude `patient` from the dep
    // array: fixture autofill should only happen once on open, not on
    // every patient-row re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patientId]);

  // Draft the PCP letter from the note text AT SEND TIME. Task #208 logic
  // unchanged: backend system CLAUDE_KEY first, legacy per-user key as
  // fallback until Task #165 finishes the migration. Returns the letter
  // text ("" on failure — callers already tolerate an empty letter: the
  // sign-anyway order path ships context without it).
  const runDraft = useCallback(async () => {
    const noteText = (getNoteText && getNoteText()) || "";
    if (!noteText.trim()) {
      setDraftError("The note is empty — nothing to draft from.");
      return "";
    }
    setIsDrafting(true);
    setDraftError(null);
    try {
      const text = await (async () => {
        try {
          const res = await fetch(`${getBackendURL()}/api/ai/complete/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              system: PCP_LETTER_SYSTEM_PROMPT,
              user: `Visit note:\n\n${noteText}`,
              max_tokens: 800,
            }),
          });
          if (res.ok) {
            const data = await res.json();
            return data.text || "";
          }
          // Backend failed — fall through to per-user key path.
        } catch (_) {
          // Network error — fall through to per-user key path.
        }
        if (!getAiKey()) {
          throw new Error(
            "Backend AI unavailable and no per-user AI key configured.",
          );
        }
        return complete({
          system: PCP_LETTER_SYSTEM_PROMPT,
          user: `Visit note:\n\n${noteText}`,
          maxTokens: 800,
        });
      })();
      setDraftLetter(text || "");
      return text || "";
    } catch (e) {
      const msg =
        e instanceof AiError
          ? e.message || "AI request failed."
          : e?.message || "Unexpected error drafting the letter.";
      setDraftError(msg);
      return "";
    } finally {
      setIsDrafting(false);
    }
    // `getNoteText` isn't memoized by the parent (see 2026-06-29 note
    // above) — safe in a click-invoked callback, unlike the old effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = useCallback(async () => {
    // v1.1: missing PCP info no longer blocks the sign. Three outcomes:
    //   (a) deliveryMethod=skip → sign-only, no letter, no order.
    //   (b) deliveryMethod=fax/email + destination filled → sign + queue.
    //   (c) deliveryMethod=fax/email + missing PCP name OR destination →
    //       sign anyway + auto-create an ORDER at the note bottom for
    //       the remote team to identify and forward. Matches the
    //       orders-row pattern from Recorder A6.2.
    const wantsToSend = deliveryMethod !== "skip";
    const destination =
      deliveryMethod === "fax"
        ? pcpFax.trim()
        : deliveryMethod === "email"
        ? pcpEmail.trim()
        : "";
    const missingInfo =
      wantsToSend && (!pcpName.trim() || !destination);

    setIsSending(true);
    try {
      // Persist whatever PCP info we DO have for future visits.
      if (pcpName.trim() || pcpFax.trim() || pcpEmail.trim()) {
        persistStoredPcp(patientId, {
          name: pcpName.trim(),
          fax: pcpFax.trim(),
          email: pcpEmail.trim(),
          deliveryMethod,
        });
      }

      // Draft-on-send (Leejoe 2026-07-18): the letter is generated HERE and
      // only because the user asked to send — sign-only never drafts. If the
      // user typed their own letter in the textarea, it is used verbatim and
      // the AI call is skipped entirely.
      let letter = draftLetter;
      if (wantsToSend && !letter.trim()) {
        letter = await runDraft();
      }

      // STUB — real backend wiring lands in Task #178 v2 spec.
      await new Promise((r) => setTimeout(r, 400));

      if (deliveryMethod === "skip") {
        toast({
          title: "No letter sent",
          description: "The note is signed. No PCP letter was sent.",
          status: "success",
          duration: 3500,
        });
      } else if (missingInfo) {
        // Sign-anyway: dispatch an order-row event for the orders section
        // at the bottom of the note. Existing orders/forwarding pattern
        // picks this up; remote team or office manager fills in the
        // PCP contact details and faxes from there.
        //
        // Leejoe 2026-06-29 — INCLUDE the AI-drafted letter in the event
        // detail. The order's whole job is "ship this letter once we
        // have contact info" — without the letter pre-attached, the
        // remote team would have to re-draft from scratch every time.
        // If the auto-draft failed (e.g. AI was down at modal-open time),
        // the order still carries the patient/visit context so the team
        // can fall back to manual draft from the chart.
        try {
          window.dispatchEvent(new CustomEvent("vmai:add-order", {
            detail: {
              kind: "forward-note",
              text: `Identify ${pcpName.trim() || "PCP"} and forward today's note (${deliveryMethod === "fax" ? "preferred: fax" : "preferred: email"}). Awaiting contact info.`,
              source: "pcp-modal-sign-anyway",
              letter: letter || "",
              pcpName: pcpName.trim(),
              preferredMethod: deliveryMethod,
            },
          }));
        } catch (_) {
          // Event dispatch failed (SSR / sandbox) — non-fatal; the
          // clinician will see the success toast either way.
        }
        toast({
          title: "Forwarding order created",
          description:
            "Missing PCP contact info — added an order at the note bottom for the remote team to identify and forward.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        const destLabel =
          deliveryMethod === "fax"
            ? `fax ${pcpFax}`
            : `email ${pcpEmail}`;
        toast({
          title: "Letter queued",
          description: `Letter queued for ${destLabel} to Dr. ${pcpName}.`,
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      }

      if (onSent) {
        onSent({
          method: deliveryMethod,
          destination,
          pcpName: pcpName.trim(),
          letter,
          orderCreated: missingInfo,
        });
      }
      onClose();
    } finally {
      setIsSending(false);
    }
  }, [
    pcpName,
    pcpFax,
    pcpEmail,
    deliveryMethod,
    patientId,
    draftLetter,
    runDraft,
    toast,
    onClose,
    onSent,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Note signed · Send copy to PCP?
          <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
            {patientName}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Stack direction={{ base: "column", md: "row" }} spacing={3}>
              <FormControl>
                <FormLabel fontSize="sm">PCP Name</FormLabel>
                <Input
                  size="sm"
                  placeholder="e.g. Dr. Michael Chen"
                  value={pcpName}
                  onChange={(e) => setPcpName(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">PCP Fax</FormLabel>
                <Input
                  size="sm"
                  placeholder="(626) 555-0178"
                  value={pcpFax}
                  onChange={(e) => setPcpFax(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">PCP Email (optional)</FormLabel>
                <Input
                  size="sm"
                  placeholder="pcp@clinic.example"
                  value={pcpEmail}
                  onChange={(e) => setPcpEmail(e.target.value)}
                />
              </FormControl>
            </Stack>

            <FormControl>
              <FormLabel fontSize="sm">Delivery method</FormLabel>
              <RadioGroup value={deliveryMethod} onChange={setDeliveryMethod}>
                <Stack direction="row" spacing={5}>
                  <Radio value="fax" size="sm">Fax</Radio>
                  <Radio value="email" size="sm">Email</Radio>
                  <Radio value="skip" size="sm">Skip — sign only</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            {deliveryMethod !== "skip" && (<>
            <Divider />

            <Box>
              <FormLabel fontSize="sm" mb={1}>
                Letter to PCP (drafts automatically when you press Send)
              </FormLabel>
              {isDrafting ? (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  py={6}
                  color="gray.500"
                >
                  <Spinner size="sm" />
                  <Text fontSize="sm">Drafting letter…</Text>
                </Box>
              ) : draftError ? (
                <Box
                  p={3}
                  bg="orange.50"
                  border="1px solid"
                  borderColor="orange.200"
                  borderRadius="md"
                  color="orange.800"
                  fontSize="sm"
                >
                  {draftError}
                </Box>
              ) : (
                <Textarea
                  value={draftLetter}
                  onChange={(e) => setDraftLetter(e.target.value)}
                  rows={10}
                  fontSize="sm"
                  fontFamily="inherit"
                  placeholder="AI drafts this from the note when you press Send — or type your own letter here and the AI draft is skipped."
                />
              )}
            </Box>
            </>)}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={isSending}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSend}
            isLoading={isSending}
            isDisabled={isDrafting && deliveryMethod !== "skip"}
          >
            {(() => {
              // Task #210: button copy reflects what actually happens on click.
              //   - skip → "Don't send"
              //   - contact info filled → "Send to PCP"
              //   - contact info missing → "Send to Orders"
              //     (routes the note + ORDERS section to the remote-entry team
              //     to identify + forward — per project-orders-section-pattern).
              //
              // No "Sign" in this copy any more: signing now happens on the
              // header's Sign & Save click, before this modal opens, so the
              // modal is purely the PCP-letter step.
              if (deliveryMethod === "skip") return "Don't send";
              const dest =
                deliveryMethod === "fax" ? pcpFax.trim()
                : deliveryMethod === "email" ? pcpEmail.trim()
                : "";
              const haveContact = pcpName.trim() && dest;
              return haveContact ? "Send to PCP" : "Send to Orders";
            })()}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
